import {
	ReactElement,
	Ref,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import { localize } from "client_legacy/ndaw/culture";
import {
	computeContextMenuTitle,
	ConfirmationDialog,
	ContextMenu,
	FloatingActionButton,
	Pagination,
	SearchInput,
	SearchPlaceholder,
	TableView,
	TableViewNoResultConfig,
	ToolbarAction,
	ToolbarRegularAction,
	useContextMenu
} from "client/Components";
import {
	deleteMultiple,
	list,
	VilansKickConnectionListItemModel
} from "client/Api/VilansKickConfiguration/vilansKickConfiguration";
import { SettingsPage } from "../../SettingsPage";
import {
	AlertManager,
	createLocalDataSource,
	MediaQueryKind,
	useAlerts,
	useDeepComparison,
	useSelection,
	useSortHandler,
	useWorkspaceMediaQuery
} from "client/Hooks";
import { DataSourceSortableState, DataSourceSortDirection } from "client/Data/sorting";
import { contains } from "client/Data/searching";
import {
	ActionKind,
	buildActionsForTopBar,
	deselectAllTitle
} from "client_legacy/ndaw/actions";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { VilansKickConnectionDialog } from "./VilansKickConnectionDialog";
import { useActiveSettingsMenuItem } from "../../SidebarMenu/sidebarMenuSelectionStore";
import { handlePageDataLoadingError } from "client/Features/ErrorPage/errorStore";
import { SettingsPageKind } from "client/Api/SettingsLayout/settingsLayout";
import { AiAssistantFabSpacing } from "client/Features/ChatAssistant/AiAssistantFabSpacing";
import { resolveFormattedTextString } from "client/Misc/utils";

type ConfigureConnectionState = {
	isVisible: boolean;
	connectionId?: number;
};

const useDataSource = createLocalDataSource<VilansKickConnectionListItemModel>()
	.setConfiguration({
		search: {
			doesMatchItem: searchMatchFunction
		},
		hasPagination: true
	})
	.getHook();

export const VilansKickConnectionPage = (): ReactElement => {

	const [isInteractingWithTheBackend, setIsInteractingWithTheBackend] = useState<boolean>(false);
	const [allItems, setAllItems] = useState<readonly VilansKickConnectionListItemModel[]>([]);
	const tableRef: Ref<HTMLDivElement> = useRef<HTMLDivElement>(null);
	const [contextMenuHandler, contextMenuProps] = useContextMenu(tableRef);

	const [configureDialogState, setConfigureDialogState] = useState<ConfigureConnectionState>({
		isVisible: false
	});
	const [itemsForDeletionConfirmation, setItemsForDeletionConfirmation] = useState<readonly VilansKickConnectionListItemModel[]>([]);

	const [sortingState, setSortingState] = useState<DataSourceSortableState<VilansKickConnectionListItemModel>>(
		{ propertyName: "name", direction: DataSourceSortDirection.Ascending }
	);
	const [currentPageNumber, setCurrentPageNumber] = useState<number>(0);
	const [searchTerm, setSearchTerm] = useState<string>("");

	const alertManager: AlertManager = useAlerts();
	const isResponsive: boolean = useWorkspaceMediaQuery(MediaQueryKind.Responsive);
	const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);
	useActiveSettingsMenuItem(SettingsPageKind.VilansKick);

	const { sortingFor } = useSortHandler<VilansKickConnectionListItemModel>(sortingState, setSortingState);
	const itemsModifiersVersion: number = useDeepComparison([sortingState, searchTerm]);

	useEffect(
		() => setCurrentPageNumber(0),
		[itemsModifiersVersion]
	);

	const { currentItems, pagination } = useDataSource(
		allItems,
		{
			sorting: sortingState,
			pagination: {
				currentPageNumber,
				amountPerPage: 25
			},
			searchTerm
		}
	);

	const [selectedItems, setSelectedItems] = useState<ReadonlyArray<VilansKickConnectionListItemModel>>([]);

	const { multiSelection, selectionFor } = useSelection(currentItems, "id", {
		isMultiSelect: true,
		selectedItems,
		setSelectedItems,
		hasMemorization: false
	});

	const selectionActions: readonly ConnectionAction[] = useMemo(
		() => getSelectionActions({
			deselectHandler: () => setSelectedItems([])
		}),
		[]
	);

	const contextualActions: readonly ConnectionAction[] = useMemo(
		() => listContextualActions(
			(item) => setConfigureDialogState({ isVisible: true, connectionId: item.id }),
			(items) => setItemsForDeletionConfirmation(items)
		),
		[]
	);

	const actionContext = useMemo(
		(): ConnectionActionContext => ({
			selection: selectedItems,
			isMobile
		}),
		[isMobile, selectedItems]
	);

	const mainAction = useMemo(
		(): ToolbarRegularAction<ConnectionActionContext> => {
			return getMainAction(
				() => setConfigureDialogState({ isVisible: true })
			);
		},
		[]
	);

	const hasSelection: boolean = actionContext.selection
		.length !== 0;

	const topBarActions = useMemo(
		(): readonly ConnectionAction[] => {
			return buildActionsForTopBar<ConnectionActionContext, ConnectionAction>(
				{
					mainActions: [mainAction],
					selectionActions,
					contextualActions
				},
				hasSelection,
				isResponsive,
				false
			);
		},
		[
			hasSelection,
			contextualActions,
			isResponsive,
			mainAction,
			selectionActions
		]
	);

	useEffect(() => {
		async function load(signal: AbortSignal): Promise<void> {
			setIsInteractingWithTheBackend(true);

			try {
				const items: VilansKickConnectionListItemModel[] = await list(signal)
					.catch(handlePageDataLoadingError);

				setAllItems(items);
				setIsInteractingWithTheBackend(false);
			} catch(e) {
				if (isCancelledError(e)) {
					return;
				}

				console.error(e);
				alertManager.showError(localize("_GeneralErrorMessage"));
				setIsInteractingWithTheBackend(false);
			}
		}

		const cancellation = new AbortController();
		load(cancellation.signal);

		return () => cancellation.abort();
	}, [alertManager]);

	const afterSaveHandler = (item: VilansKickConnectionListItemModel): void => {
		setAllItems(allItems => {
			const existingItemIndex: number = allItems.findIndex(i => i.id === item.id);
			const copy: VilansKickConnectionListItemModel[] = [...allItems];

			if (existingItemIndex === -1) {
				copy.push(item);
			} else {
				copy.splice(existingItemIndex, 1, item);
			}

			return copy;
		});
	};

	const confirmDeletionHandler = async (): Promise<void> => {
		const items: readonly VilansKickConnectionListItemModel[] = itemsForDeletionConfirmation;
		setItemsForDeletionConfirmation([]);
		setIsInteractingWithTheBackend(true);

		try {
			await deleteMultiple(items.map(i => i.id));
			setAllItems(x => x.filter(i => !items.some(di => i.id === di.id)));
		} catch (e) {
			console.error(e);
			alertManager.showError(localize("_ErrorDeletingConnections"));
		} finally {
			setIsInteractingWithTheBackend(false);
		}
	};

	const noResultConfig = useMemo(
		(): TableViewNoResultConfig => {
			if (searchTerm) {
				return {
					icon: "magnifying-glass",
					title: localize("_NoSearchResults"),
					message: localize("_PleaseTrySearchingWithAnotherTerm")
				};
			}

			return {
				icon: "gear",
				title: resolveFormattedTextString({
					template: localize("_NoXIntegrationConnectionsYet"),
					replacements: [
						{
							what: "{{IntegrationName}}",
							filler: localize("_VilansKick")
						}
					]
				}),
				message: resolveFormattedTextString({
					template: localize("_ThereAreNoXIntegrationConnectionsYet"),
					replacements: [
						{
							what: "{{IntegrationName}}",
							filler: localize("_VilansKick")
						}
					]
				})
			};
		},
		[searchTerm]
	);

	return (
		<>
			<SettingsPage
				hasBusyIndicator={isInteractingWithTheBackend}
				actions={topBarActions}
				actionContext={actionContext}
				hasSelection={hasSelection}
				title={localize("_VilansKickConnections")}
			>
				<SettingsPage.Section
					height="stretch"
				>
					<SettingsPage.SearchSection>
						<SearchInput
							onChange={setSearchTerm}
							icon="filter"
							height="large"
							placeholder={(
								<SearchPlaceholder
									kind="filter"
									properties={[localize("_Name")]}
								/>
							)}
						/>
					</SettingsPage.SearchSection>
					<SettingsPage.Section height="stretch">
						<TableView
							multiSelectable={multiSelection}
							items={currentItems}
							itemKey="id"
							height="stretch"
							columns={[
								{
									title: localize("_Name"),
									sortable: sortingFor("name"),
									trackingId: "name"
								}
							]}
							trackingId="vilans-kick-connections"
							ref={tableRef}
							noResultConfig={noResultConfig}
						>
							{item => (
								<TableView.Row
									selectable={selectionFor(item)}
									trackingId={item.id.toString()}
									onExecute={() => setConfigureDialogState({ isVisible: true, connectionId: item.id })}
									onContextMenu={contextMenuHandler}
								>
									<TableView.Cell>{item.name}</TableView.Cell>
								</TableView.Row>
							)}
						</TableView>
						<Pagination {...pagination} onPageNumberChanged={setCurrentPageNumber} />
						{contextMenuProps && (
							<ContextMenu<ConnectionActionContext>
								{...contextMenuProps}
								actions={contextualActions}
								context={actionContext}
								trackingId="vilans-kick-connection-overview-context-menu"
								title={computeContextMenuTitle(
									actionContext.selection,
									(x) => x.name
								)}
							/>
						)}
					</SettingsPage.Section>
				</SettingsPage.Section>
				{mainAction.canExecute(actionContext) && (
					<>
						<div className="preserve-space-for-mobile-plus-button"/>
						<FloatingActionButton
							onClick={() => mainAction.execute(actionContext)}
							icon="plus"
							trackingId="new-button"
						/>
					</>
				)}
				<AiAssistantFabSpacing />
			</SettingsPage>
			{configureDialogState.isVisible && (
				<VilansKickConnectionDialog
					connectionId={configureDialogState.connectionId}
					onClose={() => setConfigureDialogState({isVisible: false})}
					onAfterSave={afterSaveHandler}
				/>
			)}

			{itemsForDeletionConfirmation.length > 0 && (
				<ConfirmationDialog
					title={
						itemsForDeletionConfirmation.length === 1
							? localize("_AreYouSureYouWantToDeleteThisConnection")
							: localize("_AreYouSureYouWantToDeleteTheseConnections")
					}
					confirmText={localize("_Delete")}
					onConfirmation={confirmDeletionHandler}
					onCancellation={() => setItemsForDeletionConfirmation([])}
					trackingId="delete-api-key-confirmation-dialog"
				/>
			)}
		</>
	);
};
VilansKickConnectionPage.displayName = "VilansKickConnectionPage";

function searchMatchFunction(
	item: VilansKickConnectionListItemModel,
	normalizedNotEmptySearchTerm: Exclude<string, "">
): boolean {
	const {
		name
	} = item;

	if (contains(name, normalizedNotEmptySearchTerm)) {
		return true;
	}

	return false;
}

type ConnectionActionContext = {
	selection: readonly VilansKickConnectionListItemModel[];
	isMobile: boolean;
};

type ConnectionAction = ToolbarAction<ConnectionActionContext>;

function listContextualActions(
	editHandler: (item: VilansKickConnectionListItemModel) => void,
	deleteHandler: (items: readonly VilansKickConnectionListItemModel[]) => void
): readonly ConnectionAction[] {
	return [
		{
			kind: ActionKind.Regular,
			name: "edit",
			title: localize("_Edit"),
			canExecute: (c) => c.selection.length === 1,
			execute: (c) => editHandler(c.selection[0]),
			icon: "pen"
		},
		{
			kind: ActionKind.Regular,
			name: "delete",
			title: localize("_Delete"),
			canExecute: (c) => c.selection.length > 0,
			execute: (c) => deleteHandler(c.selection),
			icon: "trash-can"
		}
	];
}

type SelectionActionsHandlers = {
	deselectHandler: () => void;
};

function getSelectionActions(handlers: SelectionActionsHandlers): readonly ConnectionAction[] {
	return [
		{
			kind: ActionKind.Regular,
			name: "deselectAll",
			title: (c) => deselectAllTitle(c.selection),
			icon: "circle-xmark",
			canExecute: (c) => c.selection.length > 0,
			execute: () => handlers.deselectHandler()
		},
		{
			kind: ActionKind.Divider,
			name: "deselect-all-divider",
			canExecute: (c) => !c.isMobile
				&& c.selection.length > 0
		}
	];
}

function getMainAction(
	newHandler: () => void
): ToolbarRegularAction<ConnectionActionContext> {
	return {
		kind: ActionKind.Regular,
		name: "createNew",
		title: localize("_New"),
		canExecute: () => true,
		execute: () => newHandler(),
		icon: "plus",
		isPrimary: true
	};
}

export const vilansKickConfigurationRouting = {
	page: "/VilansKickConfiguration"
};